import { formatDisplayDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ExcelFileMetadata } from 'store/organisation/models';
import {
  PwaFilterChips,
  PwaRow,
  PwaSearch,
  PwaSection,
  PwaSegmented,
} from './ui';
import { PwaReceiptThumb } from './PwaReceiptThumb';

export type PwaReviewQueue = 'review' | 'post' | 'posted';
export type PwaReviewSort = 'newest' | 'oldest';

export type PwaReviewPageProps = {
  queue: PwaReviewQueue;
  onQueueChange: (queue: PwaReviewQueue) => void;
  counts: { review: number; post: number };
  showPosted: boolean;
  /** Hide CAPTURED/REVIEWED/POSTED segmented control (e.g. Excel entities). */
  showQueue?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sort?: PwaReviewSort;
  onSortChange?: (sort: PwaReviewSort) => void;
  files: ExcelFileMetadata[];
  /** Entity connection used to load each file's receipt preview. */
  connectionId?: string;
  onOpenFile: (excelMetadataId: string) => void;
  /** Extra toolbar under the list (e.g. post-to-provider actions). */
  toolbar?: React.ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
};

const toDisplayDate = (uploadDate: number | string | null | undefined) => {
  if (uploadDate == null || uploadDate === '') return undefined;
  return typeof uploadDate === 'number' ? new Date(uploadDate) : uploadDate;
};

export const PwaReviewPage: React.FC<PwaReviewPageProps> = ({
  queue,
  onQueueChange,
  counts,
  showPosted,
  showQueue = true,
  search,
  onSearchChange,
  sort = 'newest',
  onSortChange,
  files,
  connectionId,
  onOpenFile,
  toolbar,
  hasMore,
  onLoadMore,
}) => {
  const { t, i18n } = useTranslation();

  const segmentedOptions = React.useMemo(() => {
    const options: { value: string; label: string; count?: number }[] = [
      {
        value: 'review',
        label: t('organisation.pwa-review-tab-to-review', {
          defaultValue: 'To Review',
        }),
        count: counts.review,
      },
      {
        value: 'post',
        label: t('organisation.pwa-review-tab-to-post', {
          defaultValue: 'To Post',
        }),
        count: counts.post,
      },
    ];
    if (showPosted) {
      options.push({
        value: 'posted',
        label: t('organisation.transactions-view-tab-posted', {
          defaultValue: 'Posted',
        }),
      });
    }
    return options;
  }, [counts.post, counts.review, showPosted, t]);

  return (
    <div data-shell="pwa" className="pwa pwa-review-host scope-pwa-review-page">
      <div className="pwa-review-toolbar">
        {showQueue ? (
          <PwaSegmented
            label={t('organisation.pwa-review-queue', {
              defaultValue: 'Queue',
            })}
            value={queue}
            onChange={(value) => onQueueChange(value as PwaReviewQueue)}
            options={segmentedOptions}
          />
        ) : (
          <span className="pwa-review-toolbar-title">
            {t('organisation.entity-transactions-title', {
              defaultValue: 'Transactions',
            })}
          </span>
        )}
      </div>

      <PwaSection>
        <PwaSearch
          id="pwa-review-search"
          placeholder={t('organisation.pwa-review-search', {
            defaultValue: 'Search supplier or file',
          })}
          value={search}
          onChange={onSearchChange}
        />
      </PwaSection>

      {onSortChange ? (
        <>
          <div style={{ height: 12 }} />
          <PwaFilterChips
            stretch
            value={sort}
            onChange={(value) => onSortChange(value as PwaReviewSort)}
            items={[
              {
                value: 'newest',
                label: t('organisation.pwa-review-sort-newest', {
                  defaultValue: 'Newest first',
                }),
              },
              {
                value: 'oldest',
                label: t('organisation.pwa-review-sort-oldest', {
                  defaultValue: 'Oldest first',
                }),
              },
            ]}
          />
        </>
      ) : null}

      {toolbar ? (
        <div className="scope-pwa-review-page-toolbar">{toolbar}</div>
      ) : null}

      <div style={{ height: 12 }} />

      <PwaSection>
        {files.length === 0 ? (
          <div className="pwa-list">
            <div className="pwa-row static">
              <span className="pwa-row-body">
                <span className="pwa-row-sub">
                  {t('organisation.file-filter-no-results', {
                    defaultValue: 'No documents in this queue.',
                  })}
                </span>
              </span>
            </div>
          </div>
        ) : (
          <div className="pwa-list">
            {files.map((file) => {
              const id = String(file.excelMetadataId ?? '');
              const txns = Number(file.totalRows) || 0;
              const uploaded = formatDisplayDate(
                toDisplayDate(file.uploadDate),
                'D MMM YYYY',
                i18n.language
              );
              // File metadata has no supplier; use file name as the primary label.
              const contact =
                (file as { contactName?: string }).contactName ||
                (file as { supplierName?: string }).supplierName ||
                null;

              return (
                <PwaRow
                  key={id || file.fileName}
                  lead={
                    <PwaReceiptThumb
                      connectionId={connectionId}
                      metadataId={id}
                    />
                  }
                  title={contact || file.fileName || '—'}
                  sub={
                    contact ? (
                      <span className="pwa-mono">{file.fileName || '—'}</span>
                    ) : undefined
                  }
                  meta={`${txns} ${
                    txns === 1
                      ? t('organisation.transaction', {
                          defaultValue: 'transaction',
                        })
                      : t('organisation.transactions', {
                          defaultValue: 'transactions',
                        })
                  } · ${uploaded || '—'}`}
                  chevron
                  onClick={() => {
                    if (id) onOpenFile(id);
                  }}
                />
              );
            })}
          </div>
        )}
        {hasMore && onLoadMore ? (
          <button
            type="button"
            className="pwa-btn secondary sm pwa-loadmore"
            onClick={onLoadMore}
          >
            {t('main.load-more', { defaultValue: 'Load more' })}
          </button>
        ) : null}
      </PwaSection>
    </div>
  );
};

export default PwaReviewPage;
